import React, { useState, useEffect, useCallback, useMemo } from "react";
import Input from "../../../components/FormInputs";
import CustomButton from "../../../components/AddButton";
import FormTitle from "../../../components/FormTitle";
import CustomModal from "../../../components/Modal";
import { Plus, cancelButton } from "../../../common/svg";
import {
  PaymentDetail,
  PaymentDetailsProps,
} from "../../../service/interfaces/props.interface";
import SecureImage from "../../../components/BucketImage/Image";

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  setPaymentDetailsData,
  register,
  errors,
  parentUnregister,
  initialPaymentDetails = [],
}) => {
  // Track forms with a more robust data structure that includes a unique ID for each form
  const [formData, setFormData] = useState<Array<{id: number, data: PaymentDetail}>>(() => {
    if (initialPaymentDetails.length > 0) {
      return initialPaymentDetails.map((detail, index) => ({
        id: index,
        data: detail || {}
      }));
    } else {
      return [{ id: 0, data: {} }];
    }
  });
  
  const [isModalOpen, setModalOpen] = useState(false);
  const [formIndexToRemove, setFormIndexToRemove] = useState<number | null>(null);
  const [removedPaymentIds, setRemovedPaymentIds] = useState<string[]>([]);
  const [nextFormId, setNextFormId] = useState(
    initialPaymentDetails.length > 0 ? initialPaymentDetails.length : 1
  );

  const getFieldError = (index: number, fieldName: keyof PaymentDetail) =>
    (errors.paymentDetails?.[index] as any)?.[fieldName]?.message || "";

  const hasFieldError = (index: number, fieldName: keyof PaymentDetail) =>
    !!(errors.paymentDetails?.[index] as any)?.[fieldName];

  const handleAddForm = () => {
    // Add a new form with unique ID
    const newId = nextFormId;
    setNextFormId(prev => prev + 1);
    setFormData(prev => [...prev, { id: newId, data: {} }]);
  };

  const openModal = (index: number) => {
    setFormIndexToRemove(index);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleConfirmRemove = () => {
    if (formIndexToRemove === null) return;

    // Get the form being removed
    const formToRemove = formData[formIndexToRemove];
    
    // Store the payment ID before removal if exists
    if (formToRemove.data?.paymentId) {
      setRemovedPaymentIds(prev => [...prev, formToRemove.data.paymentId!]);
    }
    
    // Unregister all form fields for this form
    // This is crucial to prevent data persistence
    parentUnregister?.(`paymentDetails.${formIndexToRemove}`);
    
    // For each form that comes after the removed one, unregister it too
    // We'll re-register them at their new positions
    for (let i = formIndexToRemove + 1; i < formData.length; i++) {
      parentUnregister?.(`paymentDetails.${i}`);
    }
    
    // Remove the form from our state
    setFormData(prev => {
      const newFormData = [...prev];
      newFormData.splice(formIndexToRemove, 1);
      return newFormData;
    });
    
    // Clean up and close modal
    setFormIndexToRemove(null);
    closeModal();
  };

  // Update parent component with current payment data
  const updatePaymentData = useCallback(() => {
    // Extract just the data part from our form data structure
    const updatedData = formData
      .map(form => {
        const isRemoved = 
          form.data?.paymentId && 
          removedPaymentIds.includes(form.data.paymentId);
        
        return isRemoved ? null : form.data;
      })
      .filter((item): item is PaymentDetail => item !== null);

    setPaymentDetailsData(updatedData);
  }, [formData, removedPaymentIds, setPaymentDetailsData]);

  // Update data when forms change
  useEffect(() => {
    updatePaymentData();
  }, [updatePaymentData]);

  return (
    <div className="bg-white p-4 rounded-[20px]">
      <div className="flex items-center justify-between">
        <FormTitle title="Payment Details" />
        <CustomButton label="" icon={Plus} onClick={handleAddForm} />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full mt-5 border-collapse">
          <tbody>
            {formData.map((form, index) => (
              <tr key={form.id} className="border-b border-gray-200">
                <td className="p-3">
                  <div className="grid md:grid-cols-4 gap-x-6 gap-y-3">
                    <Input
                      key={`accountHolderName-${form.id}`}
                      label="Account Name"
                      name={`paymentDetails[${index}].accountHolderName`}
                      type="text"
                      errors={
                        hasFieldError(index, "accountHolderName")
                          ? {
                              [`paymentDetails[${index}].accountHolderName`]: {
                                message: getFieldError(
                                  index,
                                  "accountHolderName"
                                ),
                              },
                            }
                          : {}
                      }
                      register={register}
                      placeholder="Enter Account Name"
                      defaultValue={form.data?.accountHolderName || ""}
                    />
                    
                    <Input
                      key={`bankAccountNumber-${form.id}`}
                      label="Account Number"
                      name={`paymentDetails[${index}].bankAccountNumber`}
                      type="number"
                      maxLength={12}
                      errors={
                        hasFieldError(index, "bankAccountNumber")
                          ? {
                              [`paymentDetails[${index}].bankAccountNumber`]: {
                                message: getFieldError(
                                  index,
                                  "bankAccountNumber"
                                ),
                              },
                            }
                          : {}
                      }
                      register={register}
                      placeholder="Enter Account Number"
                      defaultValue={form.data?.bankAccountNumber || ""}
                    />
                    
                    <div key={`ifscCode-${form.id}`}>
                      <Input
                        label="IFSC Code"
                        name={`paymentDetails[${index}].ifscCode`}
                        type="text"
                        maxLength={11}
                        errors={
                          hasFieldError(index, "ifscCode")
                            ? {
                                [`paymentDetails[${index}].ifscCode`]: {
                                  message: getFieldError(
                                    index,
                                    "ifscCode"
                                  ),
                                },
                              }
                            : {}
                        }
                        register={register}
                        placeholder="Enter IFSC Code"
                        defaultValue={form.data?.ifscCode || ""}
                        uppercase
                      />
                    </div>
                    
                    <Input
                      key={`bankName-${form.id}`}
                      label="Bank Name"
                      name={`paymentDetails[${index}].bankName`}
                      type="text"
                      errors={
                        hasFieldError(index, "bankName")
                          ? {
                              [`paymentDetails[${index}].bankName`]: {
                                message: getFieldError(
                                  index,
                                  "bankName"
                                ),
                              },
                            }
                          : {}
                      }
                      register={register}
                      placeholder="Enter Bank Name"
                      defaultValue={form.data?.bankName || ""}
                    />
                    
                    <Input
                      key={`accountType-${form.id}`}
                      label="Account Type"
                      name={`paymentDetails[${index}].accountType`}
                      type="text"
                      errors={
                        hasFieldError(index, "accountType")
                          ? {
                              [`paymentDetails[${index}].accountType`]: {
                                message: getFieldError(
                                  index,
                                  "accountType"
                                ),
                              },
                            }
                          : {}
                      }
                      register={register}
                      placeholder="Enter Account Type"
                      defaultValue={form.data?.accountType || ""}
                    />
                    
                    <Input
                      key={`upiNumber-${form.id}`}
                      label="UPI Number"
                      name={`paymentDetails[${index}].upiNumber`}
                      type="number"
                      maxLength={10}
                      errors={
                        hasFieldError(index, "upiNumber")
                          ? {
                              [`paymentDetails[${index}].upiNumber`]: {
                                message: getFieldError(
                                  index,
                                  "upiNumber"
                                ),
                              },
                            }
                          : {}
                      }
                      register={register}
                      placeholder="Enter UPI Number"
                      defaultValue={form.data?.upiNumber || ""}
                    />
                    
                    <Input
                      key={`upiAddress-${form.id}`}
                      label="UPI ID"
                      name={`paymentDetails[${index}].upiAddress`}
                      type="text"
                      errors={
                        hasFieldError(index, "upiAddress")
                          ? {
                              [`paymentDetails[${index}].upiAddress`]: {
                                message: getFieldError(
                                  index,
                                  "upiAddress"
                                ),
                              },
                            }
                          : {}
                      }
                      register={register}
                      placeholder="Enter UPI ID"
                      defaultValue={form.data?.upiAddress || ""}
                    />
                    
                    <Input
                      key={`bankBranch-${form.id}`}
                      label="Bank Branch"
                      name={`paymentDetails[${index}].bankBranch`}
                      type="text"
                      errors={
                        hasFieldError(index, "bankBranch")
                          ? {
                              [`paymentDetails[${index}].bankBranch`]: {
                                message: getFieldError(
                                  index,
                                  "bankBranch"
                                ),
                              },
                            }
                          : {}
                      }
                      register={register}
                      placeholder="Enter Bank Branch"
                      defaultValue={form.data?.bankBranch || ""}
                    />
                  </div>
                </td>
                <td className="p-3 align-top">
                  <div className="flex items-center justify-end mb-2 gap-4">
                    <p className="text-[14px] font-[600]">
                      #{(index + 1).toString().padStart(2, "0")}
                    </p>
                    {formData.length > 1 && (
                      <button
                        onClick={() => openModal(index)}
                        type="button"
                        className="bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-all"
                      >
                        <SecureImage
                          fileKey={cancelButton}
                          alt="down arrow"
                          width="15px"
                        />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center">
            <p className="text-[18px] font-[600] text-[#4B4B4B]">
              Are you sure you want to remove this form?
            </p>
            <div className="mt-4 flex gap-4">
              <button
                onClick={handleConfirmRemove}
                type="button"
                className="bg-red-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                Yes
              </button>
              <button
                onClick={closeModal}
                type="button"
                className="bg-gray-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                No
              </button>
            </div>
          </div>
        }
        onClose={closeModal}
      />
    </div>
  );
};

export default PaymentDetails;